// The donor's image-box size map, split out of `item-image-box.tsx` for the same
// reason `button-variants.ts` is split out of `button.tsx`: a file that exports
// anything but components loses fast refresh.

/** `IMAGE_SIZE_MAP` (`item-image-box.tsx:14-20`), rung for rung. */
export const ITEM_IMAGE_BOX_SIZES = {
  xsmall: { px: 32, className: "h-8 w-8" },
  small: { px: 64, className: "h-16 w-16" },
  medium: { px: 128, className: "h-32 w-32" },
  large: { px: 256, className: "h-64 w-64" },
  xlarge: { px: 512, className: "h-128 w-128" },
} as const;

export type ItemImageBoxSize = keyof typeof ITEM_IMAGE_BOX_SIZES;

/**
 * The rung's pixel count, for a caller that needs to state `sizes`.
 *
 * **One entry per rung carrying both spellings of the number**, because the box
 * needs it as a class and `srcset` needs it as a pixel count — and the two must
 * not be able to disagree. A box sized by a Tailwind class with a `sizes`
 * guessed beside it is how a 128px slot ends up downloading the 1024px
 * rendition.
 */
export function itemImageBoxWidth(size: ItemImageBoxSize): number {
  return ITEM_IMAGE_BOX_SIZES[size].px;
}

/**
 * The house line illustration an item with no images shows.
 *
 * `timmy/src/images/Home_Illustration.svg`, copied byte for byte into
 * `client/public/` — 41x48 intrinsic. Referenced by root-relative path, which is
 * how every other SVG in this app travels.
 */
export const ITEM_PLACEHOLDER_ILLUSTRATION = "/item-placeholder-illustration.svg";

/**
 * The details gallery's thumbnail cell — `ImagePreview.tsx:58`, to the pixel.
 *
 * Not a rung: 68 is not in `IMAGE_SIZE_MAP`, because the donor's gallery does
 * not reach for its shared box here at all. It states its own 68px square, and
 * `ItemImageBox`'s `boxPx` is the escape hatch that carries it over.
 */
export const ITEM_THUMBNAIL_PX = 68;

/**
 * The gallery's shared border — `1px solid #E8E8E8` on the main image and every
 * thumbnail alike (`ImagePreview.tsx:38-43,64`).
 *
 * Its own token because the donor draws three different greys across these
 * boxes and this is none of the other two (`index.css`).
 */
export const ITEM_GALLERY_BORDER = "border-lennar-gallery-image-border border rounded-[4px]";

/**
 * How far to magnify `ITEM_PLACEHOLDER_IMAGE` so its mark reads at the size
 * modsy's reads at.
 *
 * Two measurements set this number, and it is wrong if either is dropped.
 * **Take both by decoding the PNGs, never off the rendered box** — at 28px a
 * screenshot cannot resolve one source pixel, and cannot tell ink from an
 * opaque ground. Measuring the box is how two earlier passes got this wrong in
 * both directions (the history is on #496).
 *
 * **The crop.** The asset is 1600x900 on a 245 grey, and its mark occupies
 * x 704-895, y 356-547 — a 192px square, dead centre. A background sized to
 * 833% of a 28px content box would draw that square edge to edge and nothing
 * else.
 *
 * **The size.** Edge to edge is not what modsy draws. Its fallback arrives as a
 * CloudFront error body: a 401x400 PNG on a `#FAFAFA` ground whose ink — every
 * pixel darker than that ground — spans x 59-343, y 71-336, so 284x266, or 71%
 * by 66% of the canvas. `object-fit: contain` into the same 28px box scales
 * that by 28/401 and lands the glyph at 19.83 x 18.57 css px, leaving about
 * four pixels of clear ground all round. Sizing our 192px mark to the same
 * width gives 590%.
 *
 * Note what "ink" means there, because it is the trap: the CDN body is opaque
 * almost everywhere — 98.8% alpha 255, the only transparency a one-pixel column
 * at each vertical edge — so the bbox of *non-transparent* pixels is nearly the
 * whole canvas and is not the glyph.
 *
 * The numbers are the measurement, and they belong beside the asset they
 * measure: re-export the asset and they travel with it, and a caller that
 * scales by some other amount is guessing. Used by `ItemPlaceholderMark`,
 * which carries the reasoning for why the crop exists at all.
 */
const PLACEHOLDER_ASSET = { width: 1600, mark: 192 } as const;
const MODSY_GLYPH = { width: 401, height: 400, ink: 284 } as const;

/**
 * The `background-size` that lands our mark at the size modsy's glyph lands at
 * in a given box.
 *
 * **The fit is an argument because the donor uses both.** Its variant tile
 * fits the picture with `object-fit: contain` (measured on the running stack)
 * and its upload-field `ImageCard` with `cover` (`image-card.tsx:52-60`).
 * `contain` takes the axis that runs out first, `cover` the one that runs out
 * last, so assuming either one everywhere gets the other slot wrong: reading
 * the 99x75 frame as `contain` draws the mark at 53px where modsy draws 70px,
 * which is what #496's cross-review caught.
 *
 * **Both of this app's slots come out at 590%, and that is not a coincidence.**
 * A percentage sizes the drawn image by width, and modsy's canvas is 401x400 —
 * square to within a quarter of a percent. So `contain` in a square tile and
 * `cover` in any box wider than 401:400 are both width-driven, and the
 * percentage reduces to `ink x asset / (canvas x mark)` with the box size
 * cancelling out. A slot that is *taller* than it is wide, or one that fits
 * with `contain` in a non-square box, would get a different number — which is
 * the reason this is derived per slot rather than shared as one literal.
 *
 * Rounded to whole percent: the residual is a few hundredths of a pixel at
 * these sizes, and a round number is what a reader can check against the
 * arithmetic above.
 */
export function itemPlaceholderMarkZoom(
  boxWidthPx: number,
  boxHeightPx: number,
  fit: "contain" | "cover",
): string {
  const byWidth = boxWidthPx / MODSY_GLYPH.width;
  const byHeight = boxHeightPx / MODSY_GLYPH.height;
  const scale = fit === "cover" ? Math.max(byWidth, byHeight) : Math.min(byWidth, byHeight);
  const targetPx = MODSY_GLYPH.ink * scale;
  const markFraction = PLACEHOLDER_ASSET.mark / PLACEHOLDER_ASSET.width;
  return `${Math.round((targetPx * 100) / (markFraction * boxWidthPx))}%`;
}

/**
 * The crop for the donor's 28px square tile — the variant strips, both of them,
 * which the donor fits with `contain`.
 *
 * Derived rather than written down, so the formula above is the only place this
 * arithmetic lives. It comes out at 590%.
 */
export const ITEM_PLACEHOLDER_MARK_ZOOM = itemPlaceholderMarkZoom(28, 28, "contain");

/**
 * The generic "no picture" panel — a grey image glyph on an off-white ground.
 *
 * `timmy/src/images/placeholder-image.png`, copied byte for byte into
 * `client/public/`. **A different asset from the illustration above**, and the
 * donor keeps them apart: the house is what a card substitutes for a missing
 * hero, while this is what the Add Variant dialog's 28px variant tiles fall back
 * to (`AddVariantIsland/components/item-details.tsx:322`). 1600x900, so it is a
 * flat ground with a small centred glyph at tile size.
 */
export const ITEM_PLACEHOLDER_IMAGE = "/item-placeholder-image.png";
